import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientes, useSectores, type Cliente, type FiltrosClientes } from '../lib/clientes';
import { usePuede } from '../lib/auth';
import { useMigas } from '../lib/migas';
import { Contenedor } from '../componentes/Contenedor';
import { Boton } from '../componentes/Boton';
import { BuscadorFiltros, type SeccionFiltro } from '../componentes/BuscadorFiltros';
import { Pestanas, PESTANAS_ESTADO, type EstadoVisible } from '../componentes/Pestanas';
import { FormularioCliente } from '../componentes/FormularioCliente';
import { LogoCliente } from '../componentes/LogoCliente';
import { banderaUrl, nombrePais, PAISES } from '../lib/paises';
import { Edificio, Escudo, Personas } from '../componentes/iconos';

/**
 * M2 — Clientes de Copathy.
 *
 * En la interfaz se llaman **Clientes**; en la base siguen siendo `empresas`
 * (D-035). Es la base del aislamiento: sin clientes no hay `empresa_id`, y sin
 * `empresa_id` no hay nada del producto que se pueda probar (D-028).
 *
 * ── Por qué la tarjeta ya no trae botones ──
 * Tenía «Ficha y código» y «Editar», y los dos abrían un modal. Con un cliente que
 * tiene datos, código, usuarios y programas, el modal se queda corto: **la tarjeta
 * lleva al detalle** y las acciones viven ahí. Una tarjeta de listado presenta,
 * no opera.
 */
export function Clientes() {
  const [texto, setTexto] = useState('');
  const [filtros, setFiltros] = useState<FiltrosClientes>({});
  // Arranca en «Activos» —es lo que se quiere el 95 % de las veces— pero eso
  // ahora es una pestaña que se ve, no un filtro escondido.
  const [estado, setEstado] = useState<EstadoVisible>('si');
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ir = useNavigate();

  useMigas([{ texto: 'Clientes' }]);

  const consulta = useClientes(texto, { ...filtros, activo: estado });
  const puedeCrear = usePuede('empresas.crear');
  const sectores = useSectores(true);

  /*
    Los países de la lista son los que REALMENTE tienen clientes, no los 243 del
    catálogo. Un filtro que ofrece Kiribati cuando no hay un solo cliente ahí
    obliga a recorrer una lista entera para descubrir que casi todo da cero.
  */
  const paisesConClientes = [
    ...new Set((consulta.data?.datos ?? []).map((c) => c.pais).filter((p): p is string => !!p)),
  ]
    .map((iso) => ({ valor: iso, texto: PAISES.find((p) => p.iso === iso)?.nombre ?? iso }))
    .sort((a, b) => a.texto.localeCompare(b.texto, 'es'));

  const secciones: SeccionFiltro[] = [
    /*
      El estado NO va acá: es una pestaña, debajo del buscador. Dentro del panel
      era un valor por omisión invisible —la lista escondía los desactivados y la
      razón estaba plegada a dos clics—.
    */
    {
      titulo: 'Perfil del cliente',
      campos: [
        {
          tipo: 'multi',
          clave: 'sector',
          texto: 'Sector',
          opciones: (sectores.data ?? []).map((s) => ({ valor: String(s.id), texto: s.nombre })),
        },
        { tipo: 'multi', clave: 'pais', texto: 'País', opciones: paisesConClientes },
      ],
    },
  ];

  return (
    <Contenedor>
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-marino-800">Clientes</h1>
          <p className="mt-1 text-sm text-marino-400">
            Las empresas a las que COPATHY les presta el servicio, y la puerta por la que se
            registra su gente.
          </p>
        </div>
      </div>

      {/*
        El botón de filtros vive DENTRO del campo de búsqueda, y lo aplicado baja
        como chips removibles. El de «Registrar» va en la misma fila: una fila
        propia para un solo botón cuesta 56 px en la zona más cara de la pantalla,
        que es justo la que decide si la lista cabe sin desplazarse.
      */}
      <div className="mb-5">
        <BuscadorFiltros
          busqueda={texto}
          alBuscar={setTexto}
          guia="Buscar por nombre, identificación fiscal o sector"
          secciones={secciones}
          valores={filtros}
          alAplicar={setFiltros}
          derecha={
            puedeCrear ? (
              <Boton onClick={() => setCreando(true)} className="py-3">
                Registrar cliente
              </Boton>
            ) : undefined
          }
        />
      </div>

      <Pestanas
        etiqueta="Estado"
        pestanas={PESTANAS_ESTADO}
        activa={estado}
        alElegir={setEstado}
      />

      {error && (
        <div
          role="alert"
          className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      {consulta.isPending && <p className="py-16 text-center text-marino-400">Cargando…</p>}

      {consulta.isError && (
        <p className="py-16 text-center text-red-600">
          No pudimos cargar los clientes. Recarga la página.
        </p>
      )}

      {consulta.data?.datos.length === 0 && (
        <div className="rounded-2xl border border-marino-100 bg-white py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-marino-50 text-marino-300">
            <Edificio size={24} />
          </div>
          <h2 className="font-semibold text-marino-700">
            {texto ? 'Ningún cliente coincide' : 'Todavía no hay clientes registrados'}
          </h2>
          <p className="mt-1 text-sm text-marino-400">
            {texto
              ? 'Prueba con otro nombre, o quita algún filtro.'
              : 'Registra el primero para poder darle su código y que su gente se registre.'}
          </p>
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {consulta.data?.datos.map((c) => (
          /*
            Es un <button> y no un <div> con onClick: así llega el foco por
            tabulador, se activa con Enter y los lectores de pantalla lo anuncian
            como algo que se puede accionar. Un div clicable no hace nada de eso.
          */
          <button
            key={c.id}
            type="button"
            onClick={() => ir(`/clientes/${c.id}`)}
            className={`group rounded-2xl border bg-white p-5 text-left transition
              hover:border-marino-300 hover:shadow-sm
              focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
              ${c.activo ? 'border-marino-100' : 'border-marino-100 opacity-70'}`}
          >
            <div className="flex items-start gap-3">
              <LogoCliente clienteId={c.id} nombre={c.nombre} tieneLogo={c.tieneLogo} />

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h2 className="font-semibold text-marino-800">{c.nombre}</h2>
                  {!c.activo && (
                    <span className="rounded-full bg-marino-50 px-2.5 py-0.5 text-xs font-semibold text-marino-500">
                      Desactivado
                    </span>
                  )}
                </div>
                <p className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-marino-400">
                  {c.pais && (
                    <>
                      <img
                        src={banderaUrl(c.pais)}
                        alt=""
                        loading="lazy"
                        className="h-3 w-4.5 rounded-[2px] object-cover"
                      />
                      <span>{nombrePais(c.pais)}</span>
                      <span aria-hidden="true">·</span>
                    </>
                  )}
                  <span>
                    {[c.sector, c.rif].filter(Boolean).join(' · ') || 'Sin datos cargados'}
                  </span>
                </p>

                <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                  <div className="flex items-center gap-1.5">
                    <Personas size={15} />
                    <dt className="sr-only">Trabajadores</dt>
                    <dd className="text-marino-700">
                      {c.trabajadores} {c.trabajadores === 1 ? 'trabajador' : 'trabajadores'}
                    </dd>
                  </div>
                  {c.empleados !== null && (
                    <div className="flex gap-1.5">
                      <dt className="text-marino-400">Empleados:</dt>
                      <dd className="text-marino-700">{c.empleados.toLocaleString('es-VE')}</dd>
                    </div>
                  )}
                  {c.solicitudesPendientes > 0 && (
                    <div className="flex items-center gap-1.5 text-naranja-700">
                      <Escudo size={15} />
                      <dt className="sr-only">Solicitudes por revisar</dt>
                      <dd className="font-semibold">{c.solicitudesPendientes} por revisar</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </button>
        ))}
      </div>

      {consulta.data && consulta.data.total > consulta.data.datos.length && (
        <p className="mt-6 text-center text-sm text-marino-400">
          Mostrando {consulta.data.datos.length} de {consulta.data.total}.
        </p>
      )}

      <FormularioCliente
        objetivo={creando ? 'nuevo' : null}
        alCerrar={() => setCreando(false)}
        alFallar={setError}
        // Recién registrado, lo lógico es entrar a su detalle: es donde se le da
        // el código de registro, que es lo que hace falta para que su gente entre.
        alGuardar={(c: Cliente) => ir(`/clientes/${c.id}`)}
      />
    </Contenedor>
  );
}
